import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaFileExcel } from 'react-icons/fa';
import { toast } from '../../toasts';
import creditPaymentService from '../../services/creditPayment.service';
import { getError, showCurrency } from '../../utils';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, StatStrip, DataTable, IdChip, RowActionsMenu, Button, ExportModal, buildXlsxExport, exportAmount } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';
import { useClientSort } from '../../hooks/useClientSort';
import { dateValue, parseDueNumber } from '../../utils/tableSort';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	if (amount === null || amount === undefined) {
		return '';
	}
	return `${showCurrency(currency) || ''} ${Number(amount).toLocaleString('es-UY')}`.trim();
}

function CreditPaymentsCredit({ creditId, rows = [], loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const location = useLocation();
	const [deletingPayment, setDeletingPayment] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'nro',
		// Several payments can pay the same due; those read in date order.
		tieBreakerColumn: 'date',
		accessors: {
			nro: (payment) => parseDueNumber(payment.creditDue?.customId),
			customId: (payment) => payment.customId,
			creditDue: (payment) => payment.creditDue?.customId,
			date: (payment) => dateValue(payment.date),
		},
	});

	const returnTo = () => encodeURIComponent(location.pathname + location.search);

	const handleCreatePayment = () => {
		navigate(`/AdminScreen/credits/credit/${creditId}/newCreditPayment?returnTo=${returnTo()}`);
	};

	const handleEditPayment = (payment) => {
		navigate(`/AdminScreen/creditPayments/editCreditPayment/${payment._id}?returnTo=${returnTo()}`);
	};

	const stats = useMemo(() => {
		const totalByCurrency = new Map();
		rows.forEach((payment) => {
			const key = payment.credit?.currency || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (payment.amount || 0));
		});
		return [
			{ label: t('entity.payment.plural'), value: rows.length },
			{
				label: 'Total cobrado',
				value:
					[...totalByCurrency.entries()].map(([currency, amount]) => formatMoney(currency, amount)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows, t]);

	async function handleConfirmDelete() {
		try {
			await creditPaymentService.delete(deletingPayment._id);
			toast.success(t('msg.paymentDeleted'));
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingPayment(null);
		}
	}

	const columns = [
		{
			key: 'nro',
			header: 'Nro',
			sortable: true,
			className: 'tw:font-mono tw:tabular-nums',
			render: (payment) => parseDueNumber(payment.creditDue?.customId) ?? <span className='tw:text-ink-3'>—</span>,
		},
		{ key: 'customId', header: t('field.id'), sortable: true, render: (payment) => <IdChip>{payment.customId}</IdChip> },
		{ key: 'creditDue', header: t('entity.due'), sortable: true, render: (payment) => payment.creditDue?.customId },
		{ key: 'date', header: t('field.date'), sortable: true, render: (payment) => formatDate(payment.date) },
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (payment) => (
				<span className='tw:font-mono tw:tabular-nums'>{formatMoney(payment.credit?.currency, payment.amount)}</span>
			),
		},
		{ key: 'folder', header: t('field.folder'), render: (payment) => payment.folder },
	];

	const exportFields = [
		{ key: 'nro', label: 'Nro' },
		{ key: 'customId', label: t('field.id'), locked: true },
		{ key: 'creditDue', label: t('entity.due') },
		{ key: 'date', label: t('field.date') },
		{ key: 'currency', label: t('field.currency') },
		{ key: 'amount', label: t('field.amount') },
		{ key: 'folder', label: t('field.folder') },
	];

	const exportGetters = {
		nro: (payment) => parseDueNumber(payment.creditDue?.customId) ?? '',
		customId: (payment) => payment.customId || '',
		creditDue: (payment) => payment.creditDue?.customId || '',
		date: (payment) => formatDate(payment.date),
		currency: (payment) => payment.credit?.currency || '',
		amount: (payment) => exportAmount(payment.amount),
		folder: (payment) => payment.folder || '',
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: sortedRows,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Cobros',
			filePrefix: 'cobros',
		});

	const renderRowActions = (payment) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => handleEditPayment(payment) },
				payment.comprobante && {
					label: t('action.viewReceipt'),
					onClick: () => window.open(payment.comprobante.fileLink, '_blank', 'noopener,noreferrer'),
				},
				{ label: t('action.delete'), onClick: () => setDeletingPayment(payment), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={rows.length === 0}>
					{t('action.export')}
				</Button>
				<Button size='sm' variant='primary' onClick={handleCreatePayment}>
					{`Nuevo ${t('entity.payment')}`}
				</Button>
			</div>
			<Section title={t('entity.payment.plural')}>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					onRowClick={(payment) => navigate(`/AdminScreen/creditPayments/creditPayment/${payment._id}`)}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron cobros para este crédito.'
				/>
			</Section>

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={rows.length}
				fields={exportFields}
				subtitle={t('entity.payment.plural')}
				onExport={handleExport}
			/>

			<ConfirmationModal
				show={Boolean(deletingPayment)}
				handleClose={() => setDeletingPayment(null)}
				handleConfirm={handleConfirmDelete}
				title={`Eliminar ${t('entity.payment')}`}
				description={t('msg.confirmDeletePayment')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default CreditPaymentsCredit;
